/*
 * djvu-job.js — one DjVu -> PDF conversion on the page: a pool of
 * djvu-worker.js instances decodes pages in parallel, this file writes the
 * PDF in page order as they come back.
 *
 *   const job = DjVuJob.create({ workerUrl, files });
 *   const info = await job.inspect(onProgress);        // directory + page survey
 *   const pic  = await job.render(index, 180);          // preview (ImageData)
 *   const out  = await job.convert({ preset, from, to, ocr, onProgress, signal });
 *   job.close();
 *
 * Why the PDF is written HERE and not in a worker: pages come back from
 * several workers and are written in order; the writer's own work is string
 * building and handing bytes to a Blob — cheap. Every heavy step (wavelet
 * decode, G4, JPEG, deflate) happens in the workers.
 *
 * Memory: pages in flight are budgeted by an estimate of what decoding them
 * costs (a 60-megapixel colour scan is not a text page), and no page runs
 * more than `ahead` pages in front of the one being written, so a slow page
 * (or OCR) never lets the rest pile up. Output goes to Blob pieces of a few
 * megabytes, which the browser may keep on disk.
 *
 * OCR (pages without a text layer): OcrEngine (the site's one OCR door) reads
 * the grey picture the worker renders; words land in an extra content stream
 * as invisible text, the same way the DjVu text layer does.
 * ES2019.
 */
(function (root) {
  'use strict';
  if (root.DjVuJob) return;

  var Pdf = function () { return root.DjVuPdf; };
  var Core = function () { return root.DjVuCore; };

  function deflateAsync(bytes) {
    if (typeof CompressionStream !== 'function') return Promise.resolve(null);
    var stream = new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate'));
    return new Response(stream).arrayBuffer().then(function (b) { return new Uint8Array(b); });
  }

  // Bytes -> Blob pieces of ~8 MB: the page never holds the whole PDF.
  function BlobSink() {
    this.parts = [];
    this.pending = [];
    this.pendingBytes = 0;
    this.total = 0;
  }
  BlobSink.prototype.write = function (b) {
    this.pending.push(b);
    this.pendingBytes += b.length;
    this.total += b.length;
    if (this.pendingBytes >= 8 * 1024 * 1024) this.flush();
  };
  BlobSink.prototype.flush = function () {
    if (!this.pending.length) return;
    this.parts.push(new Blob(this.pending));
    this.pending = [];
    this.pendingBytes = 0;
  };
  BlobSink.prototype.blob = function () {
    this.flush();
    return new Blob(this.parts, { type: 'application/pdf' });
  };

  function cancelError() {
    var e = new Error('cancelled');
    e.name = 'AbortError';
    e.code = 'USER_CANCELLED';
    return e;
  }

  // How much memory to assume: the site's one answer (window.__memBudget in
  // the layout) — WebKit has no navigator.deviceMemory at all, and a tab on
  // iOS is killed at ~500–700 MB whatever the phone's RAM, so there one
  // worker and a small page budget.
  function budget() {
    var B = typeof window !== 'undefined' && window.__memBudget;
    return { gb: B ? B.gb() : 4, constrained: !!(B && B.constrained()) };
  }
  function poolSize() {
    var cores = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 2;
    var m = budget();
    var n = Math.max(1, Math.min(4, cores - 1));
    if (m.constrained || m.gb <= 2) n = Math.min(n, 1);
    else if (m.gb <= 4) n = Math.min(n, 2);
    return n;
  }
  function memoryBudget() {
    var m = budget();
    if (m.constrained) return 256 * 1024 * 1024;
    return Math.max(256, Math.min(1536, m.gb * 1024 * 0.25)) * 1024 * 1024;
  }
  // Rough peak cost of decoding one page: the mask and its stencils, plus
  // the wavelet background (three coefficient planes and their output).
  function pageCost(p) {
    if (!p || !p.width) return 64 * 1024 * 1024;
    var px = p.width * p.height;
    var bg = p.bgSize ? p.bgSize.width * p.bgSize.height : 0;
    return px * 1.2 + bg * 22 + 16 * 1024 * 1024;
  }

  // One worker started while the page is idle: its scripts load while the
  // person is still picking a file, and the first book takes it over.
  var warm = null;
  function prewarm(url) {
    if (warm || typeof Worker !== 'function' || !url) return;
    try { warm = { url: url, worker: new Worker(url) }; } catch (e) { warm = null; }
  }

  function create(opts) {
    var files = opts.files;
    var main = opts.main || 0;
    var workerUrl = opts.workerUrl;
    var workers = [];
    var seq = 0;
    var pending = {};          // id -> {resolve, reject}
    var info = null;
    var closed = false;

    function spawn() {
      var w;
      if (warm && warm.url === workerUrl) { w = warm.worker; warm = null; }
      else w = new Worker(workerUrl);
      w.onmessage = function (ev) {
        var m = ev.data || {};
        if (m.type === 'progress') { if (w.__onProgress) w.__onProgress(m); return; }
        var p = pending[m.id];
        if (!p) return;
        delete pending[m.id];
        if (m.type === 'error') {
          var e = new Error(m.message || 'DjVu error');
          e.code = m.code;
          e.pageIndex = m.index;
          p.reject(e);
        } else p.resolve(m);
      };
      w.onerror = function (ev) {
        // a worker that dies takes its jobs with it
        var e = new Error((ev && ev.message) || 'worker failed');
        e.code = 'WORKER_DIED';
        Object.keys(pending).forEach(function (id) {
          if (pending[id].worker === w) { var p = pending[id]; delete pending[id]; p.reject(e); }
        });
        if (ev && ev.preventDefault) ev.preventDefault();
      };
      workers.push(w);
      return w;
    }
    function call(w, msg) {
      var id = ++seq;
      msg.id = id;
      return new Promise(function (resolve, reject) {
        pending[id] = { resolve: resolve, reject: reject, worker: w };
        w.postMessage(msg);
      });
    }
    // A worker knows the book once it has read or adopted the directory;
    // workers spawned after a cancel (which terminates them all) adopt it.
    async function opened(w) {
      if (!w.__opened && info) {
        await call(w, { type: 'open', files: files, main: main, dir: info.dir });
        w.__opened = true;
      }
      return w;
    }
    async function first() {
      closed = false;
      return opened(workers[0] || spawn());
    }

    async function inspect(onProgress) {
      var w = workers[0] || spawn();
      closed = false;
      w.__onProgress = onProgress || null;
      info = await call(w, { type: 'inspect', files: files, main: main });
      w.__onProgress = null;
      w.__opened = true;
      return info;
    }

    async function render(index, width) {
      var w = await first();
      var r = await call(w, { type: 'render', index: index, width: width });
      return { width: r.width, height: r.height, data: r.data, rotation: r.rotation };
    }

    async function ensurePool(n) {
      closed = false;
      await first();
      while (workers.length < n) await opened(spawn());
    }

    // OCR words -> text runs in DjVu px (origin bottom-left).
    function ocrRuns(data, img, pageH, minConf) {
      var runs = [];
      var sc = img.scale || 1;
      var lines = data && data.lines && data.lines.length ? data.lines : null;
      var li = 0;
      function push(wd, line) {
        var t = String(wd.text || '').replace(/\s+/g, ' ').trim();
        if (!t || !wd.bbox) return;
        if (typeof wd.confidence === 'number' && wd.confidence < minConf) return;
        var b = wd.bbox;
        runs.push({
          text: t,
          xmin: Math.round(b.x0 * sc), xmax: Math.round(b.x1 * sc),
          ymin: Math.round(pageH - b.y1 * sc), ymax: Math.round(pageH - b.y0 * sc),
          line: line
        });
      }
      if (lines) {
        for (var i = 0; i < lines.length; i++) {
          var ws = lines[i].words || [];
          li++;
          for (var j = 0; j < ws.length; j++) push(ws[j], li);
        }
      } else if (data && data.words) {
        for (var k = 0; k < data.words.length; k++) push(data.words[k], ++li);
      }
      return runs;
    }

    async function recognise(engine, img, pageH, signal) {
      var n = img.width * img.height;
      var rgba = new Uint8ClampedArray(n * 4);
      var g = img.data;
      for (var i = 0; i < n; i++) { var v = g[i]; rgba[i * 4] = v; rgba[i * 4 + 1] = v; rgba[i * 4 + 2] = v; rgba[i * 4 + 3] = 255; }
      var bmp = await createImageBitmap(new ImageData(rgba, img.width, img.height));
      if (signal && signal.aborted) throw cancelError();
      var data = await engine.recognize(bmp, { orient: false });
      return ocrRuns(data, img, pageH, 35);
    }

    async function convert(o) {
      var P = Pdf(), C = Core();
      var signal = o.signal || null;
      var dir = info.dir;
      var from = Math.max(0, o.from || 0);
      var to = Math.min(dir.pages.length - 1, o.to == null ? dir.pages.length - 1 : o.to);
      var count = to - from + 1;
      var pdfIndex = function (djvuIndex) { return djvuIndex >= from && djvuIndex <= to ? djvuIndex - from : -1; };
      var nWorkers = Math.min(poolSize(), Math.max(1, count));
      await ensurePool(nWorkers);
      if (signal && signal.aborted) throw cancelError();

      var sink = new BlobSink();
      var writer = new P.PdfWriter({ sink: function (b) { sink.write(b); }, deflate: null });
      writer.reservePages(count);
      try { writer.fontStreams = await P.fontStreams(deflateAsync); } catch (e) { writer.fontStreams = null; }

      var ocr = o.ocr && o.ocr.engine ? o.ocr : null;
      var stats = { pages: count, words: 0, textPages: 0, ocrPages: 0, ocrWords: 0, links: 0, bytesIn: 0, kinds: {}, encoder: null, partial: 0, failed: [] };
      var budget = memoryBudget();
      var ahead = Math.max(4, nWorkers * 3);
      var next = from;          // next DjVu page to dispatch
      var writeAt = from;       // next DjVu page to write
      var inFlight = 0, inFlightCost = 0;
      var ready = {};           // djvu index -> result
      var idle = workers.slice(0, nWorkers);
      var t0 = Date.now();

      return await new Promise(function (resolve, reject) {
        var done = false;
        function finish(err, value) {
          if (done) return;
          done = true;
          if (signal) signal.removeEventListener('abort', onAbort);
          if (err) reject(err); else resolve(value);
        }
        function onAbort() {
          // workers stop mid-page only by being replaced
          close();
          finish(cancelError());
        }
        if (signal) signal.addEventListener('abort', onAbort);

        function dispatch() {
          while (!done && idle.length && next <= to && (next - writeAt) < ahead) {
            var cost = pageCost(info.pages[next]);
            if (inFlight > 0 && inFlightCost + cost > budget) break;
            var w = idle.shift();
            var index = next++;
            inFlight++;
            inFlightCost += cost;
            (function (w, index, cost) {
              call(w, { type: 'page', index: index, preset: o.preset, masks: o.masks || 'jbig2', ocr: ocr ? { maxSide: 3000 } : null })
                .then(function (r) { ready[index] = r; }, function (err) {
                  if (err && err.code === 'WORKER_DIED') { finish(err); return; }
                  ready[index] = { failed: true, error: err };
                })
                .then(function () {
                  inFlight--;
                  inFlightCost -= cost;
                  idle.push(w);
                  pump();
                });
            })(w, index, cost);
          }
        }

        var writing = false;
        async function writeReady() {
          if (writing) return;
          writing = true;
          try {
            while (!done && ready[writeAt]) {
              var r = ready[writeAt];
              delete ready[writeAt];
              var spec;
              if (r.failed) {
                // a page that cannot be decoded becomes a blank page of the
                // right size, and the result says which one
                var pi = info.pages[writeAt] || {};
                stats.failed.push(writeAt + 1);
                spec = { index: 0, width: pi.width || 2480, height: pi.height || 3508, dpi: pi.dpi || 300, rotation: 0, masks: [], runs: [] };
              } else {
                spec = r.spec;
                var st = r.stats;
                stats.kinds[st.kind] = (stats.kinds[st.kind] || 0) + 1;
                if (st.words) { stats.words += st.words; stats.textPages++; }
                if (st.partial) stats.partial++;
                if (r.encoder) stats.encoder = r.encoder;
                if (r.ocrImage && ocr) {
                  if (o.onProgress) o.onProgress({ phase: 'ocr', page: writeAt - from + 1, total: count });
                  var runs = [];
                  try { runs = await recognise(ocr.engine, r.ocrImage, spec.height, signal); }
                  catch (e) { if (e && e.code === 'USER_CANCELLED') throw e; runs = []; stats.ocrFailed = (stats.ocrFailed || 0) + 1; }
                  r.ocrImage = null;
                  if (runs.length) {
                    var ops = P.textOps(runs, 72 / spec.dpi);
                    var raw = P.latin1(ops);
                    var z = await deflateAsync(raw).catch(function () { return null; });
                    spec.extra = [z && z.length < raw.length ? { bytes: z, z: true } : { bytes: raw, z: false }];
                    spec.hasText = true;
                    stats.ocrPages++;
                    stats.ocrWords += runs.length;
                  }
                }
              }
              spec.index = writeAt - from;
              // links point at pages of the PDF, which may be a range
              if (spec.links && spec.links.length) {
                spec.links = spec.links.filter(function (l) {
                  if (typeof l.page !== 'number') return true;
                  var t = pdfIndex(l.page);
                  if (t < 0) return false;
                  l.page = t;
                  return true;
                });
                stats.links += spec.links.length;
              }
              P.assemblePage(writer, spec);
              writeAt++;
              if (o.onProgress) {
                o.onProgress({ phase: 'pages', done: writeAt - from, total: count, elapsed: Date.now() - t0, bytes: sink.total });
              }
            }
            if (!done && writeAt > to) {
              writer.finish({
                info: o.info || {},
                outline: outlineFor(dir.outline, dir, pdfIndex, C),
                labels: labelsFor(info.pages, from, to),
                labelStart: from + 1,
                lang: o.lang || null
              });
              stats.bytes = sink.total;
              stats.ms = Date.now() - t0;
              finish(null, { blob: sink.blob(), stats: stats });
            }
          } catch (err) {
            finish(err);
          } finally {
            writing = false;
          }
          if (!done) pump();
        }

        function pump() {
          if (done) return;
          dispatch();
          if (ready[writeAt]) writeReady();
        }
        pump();
      });
    }

    function close() {
      closed = true;
      for (var i = 0; i < workers.length; i++) { try { workers[i].terminate(); } catch (e) { /* gone */ } }
      workers = [];
      var e = cancelError();
      Object.keys(pending).forEach(function (id) { var p = pending[id]; delete pending[id]; p.reject(e); });
    }

    return {
      inspect: inspect,
      render: render,
      convert: convert,
      close: close,
      get closed() { return closed; },
      get info() { return info; }
    };
  }

  // DjVu outline -> PDF outline for the converted range.
  function outlineFor(nodes, dir, pdfIndex, C) {
    var out = [];
    for (var i = 0; i < (nodes || []).length; i++) {
      var n = nodes[i];
      var r = C.resolveUrl(n.url, dir);
      var kids = outlineFor(n.children, dir, pdfIndex, C);
      var item = { title: n.title || '', children: kids };
      if (r && typeof r.page === 'number') {
        var t = pdfIndex(r.page);
        if (t >= 0) item.page = t;
      } else if (r && r.external) item.url = r.external;
      if (item.page == null && !item.url && !kids.length) continue;
      out.push(item);
    }
    return out;
  }

  function labelsFor(pages, from, to) {
    var out = [];
    for (var i = from; i <= to; i++) out.push(pages[i] && pages[i].title ? pages[i].title : null);
    return out;
  }

  root.DjVuJob = { create: create, prewarm: prewarm, _internal: { outlineFor: outlineFor, labelsFor: labelsFor, BlobSink: BlobSink, pageCost: pageCost } };
})(typeof self !== 'undefined' ? self : this);
